// Phase 31 Plan 31-03 Task 2 — FirstAnchorTutorialModal.
//
// One-shot lifetime tutorial dialog that fires the first time a player's
// Anchor is CONSUMED (server signals via PlayerView.tutorial_anchor_-
// consumption_first_time === true). The server clears the flag atomically
// with the consumption side-effect (Phase 26 D-LOCK) so the modal never
// re-mounts across sessions. MatchScreen (Task 3) adds a session-ref guard
// for replay-safety against cached snapshots.
//
// Recipe: role=dialog + aria-modal + useFocusTrap (via <Dialog> primitive).
// Diagram cells use simplified static styled divs (NOT Pixi-inside-DOM) per
// the planner discretion in plan §<behavior> — the modal is a one-shot
// informational surface, not a precise visual replica of in-game cells.
// Future enhancement: replace with Pixi LetterCubeSprite primitives inside
// an <Application> mount per UI-SPEC §1 row 4 / PATTERNS §5.
//
// CRITICAL — T-31-04 lock: NO browser-storage fallback (no local/session
// storage references). The server-side one-shot flag is the single source
// of truth; cross-device coherent.
//
// ARCA-82 WS2 — migrated to <Dialog> primitive (ModalScrim): scrim/panel
// styling, focus trap, and dismissal contract live in the primitive.

import type { JSX } from 'react'
import { FormattedMessage } from 'react-intl'

import { Btn } from '../components/Btn'
import { Dialog } from '../components/Dialog'
import { Icon } from '../components/Icon'

export interface FirstAnchorTutorialModalProps {
  readonly onDismiss: () => void
}

export function FirstAnchorTutorialModal({
  onDismiss,
}: FirstAnchorTutorialModalProps): JSX.Element {
  // ARCA-82 WS2 — useFocusTrap + scrim/panel styling live in <Dialog>/ModalScrim.

  return (
    // Center-dialog flavor (ARCA-82 WS2): scrim + popIn + focus trap +
    // dismissal live in <Dialog>/ModalScrim. padding:0 so the bordered header
    // bleeds to the rounded panel edge (overflow:hidden clips it).
    <Dialog
      onClose={onDismiss}
      labelledBy="anchor-tutorial-title"
      describedBy="anchor-tutorial-body"
      panelStyle={{ padding: 0, overflow: 'hidden' }}
    >
      <header
        style={{
          padding: 'var(--space-4)',
          borderBottom: '2px solid var(--color-surface-stroke)',
        }}
      >
        <h2
          id="anchor-tutorial-title"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-display)',
            fontWeight: 700,
            fontSize: 'var(--font-size-22)',
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage id="match.tutorial.anchor.title" />
        </h2>
      </header>
      <div
        style={{
          padding: 'var(--space-4)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        {/* Anchor + Hollow mock cells — simplified static styled divs (v1).
              Future enhancement: replace with Pixi LetterCubeSprite primitives
              inside an <Application> mount per UI-SPEC §1 row 4 / PATTERNS §5. */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-4)',
            justifyContent: 'center',
          }}
          aria-hidden="true"
        >
          <div
            data-tutorial-mock="anchor"
            style={{
              width: 60,
              height: 60,
              background: 'var(--color-surface-stroke)',
              border: '3px solid var(--color-cta-primary)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-22)',
              color: 'var(--color-cta-primary)',
            }}
          >
            {'⚓'}
          </div>
          <div
            data-tutorial-mock="hollow"
            style={{
              width: 60,
              height: 60,
              background: 'var(--color-surface-deep)',
              border: '2px dashed var(--color-surface-stroke)',
              borderRadius: 'var(--radius-md)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-22)',
              color: 'var(--color-text-secondary)',
              opacity: 0.6,
            }}
          >
            <Icon name="close" size={22} />
          </div>
        </div>
        <p
          id="anchor-tutorial-body"
          style={{
            margin: 0,
            fontFamily: 'var(--font-family-body)',
            fontWeight: 600,
            fontSize: 'var(--font-size-14)',
            lineHeight: 1.5,
            color: 'var(--color-text-primary)',
          }}
        >
          <FormattedMessage id="match.tutorial.anchor.body" />
        </p>
        <Btn kind="gold" size="lg" block onClick={onDismiss}>
          <FormattedMessage id="match.tutorial.anchor.cta_dismiss" />
        </Btn>
      </div>
    </Dialog>
  )
}
